import React, { useEffect, useState, useMemo } from "react";
import { useTheme } from "../../../../../context/ThemeContext";
import ChartsWebex from "../../Charts/ChartsWebex";
import Loader from "../../../../../components/Loader";
import { StackedLineChart, StackedBarChart } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { convertDateWithDayName } from '../../../../../scripts/utils';

export default function TypeTrendsPost({ data, people, setDataGraph, setFormattedHeader, filteredPosts, user }) {
    const { theme } = useTheme();
    const [typeTrends, setTypeTrends] = useState(null);
    const [dataTypeTrends, setDataTypeTrends] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPostPage, setCurrentPostPage] = useState({});
    const [postItemsPerPage, setPostItemPerPage] = useState(5);
    const [selectedTypeChart, setSelectedTypeChart] = useState('trends-bar');
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language;

    // 1. RECUPERATION DU FILTRE NOMINATIF (Utile QUE pour Holding 33)
    const normalizedFilteredPosts = useMemo(() => {
        if (Array.isArray(filteredPosts)) {
            const personNames = filteredPosts
                .map(entry => entry.personName?.trim().toLowerCase())
                .filter(Boolean);
            return { personNames };
        }
        return { personNames: [] };
    }, [filteredPosts]);

    useEffect(() => {
        if (data.length > 0) {
            handleGraphTypeTrends(data);
            setCurrentPostPage(1);
        }
    }, [data, user, filteredPosts]);

    useEffect(() => {
        if (typeTrends) {
            handleGraphDataTypeTrends(typeTrends.maxCallsData);
            setCurrentPage(1);
        }
    }, [typeTrends, user, filteredPosts]);

    // ---- UTILITAIRES DE RECHERCHE ET DE VALIDATION ----
    const checkNameMatch = (itemName, person) => {
        if (!itemName) return false;
        const p1 = `${person.firstName || ''} ${person.lastName || ''}`.trim().toLowerCase();
        const p2 = `${person.lastName || ''} ${person.firstName || ''}`.trim().toLowerCase();
        const itemLower = itemName.toLowerCase();

        return itemLower === p1 ||
            itemLower === p2 ||
            itemLower.includes(p1) ||
            itemLower.includes(p2) ||
            p1.includes(itemLower) ||
            p2.includes(itemLower);
    };

    const getPeopleByNumberMap = (peopleFlat) => {
        const map = new Map();
        peopleFlat.forEach(p => {
            [p.phone?.work, p.phone?.mobile].filter(Boolean).forEach(num => {
                if (!map.has(num)) map.set(num, []);
                map.get(num).push(p);
            });
        });
        return map;
    };

    // IDENTIFIE ET FILTRE STRICTEMENT (UNIQUEMENT POUR HOLDING 33)
    const extractCallEntity = (item, knownNumbers, peopleByNumber, canUseCallerId, personNames) => {
        const isOutgoing = item.direction === "ORIGINATING";
        const isIncoming = item.direction === "TERMINATING";

        if (!isOutgoing && !isIncoming) return null;

        let matchedPost = null;
        let matchedName = "";
        let isCallerIdMatch = false;

        if (isOutgoing) {
            const workOrMobile = item.callingnumber;
            if (knownNumbers.has(workOrMobile)) {
                matchedPost = (canUseCallerId && item.calleridnumber) ? item.calleridnumber : workOrMobile;
                isCallerIdMatch = (canUseCallerId && !!item.calleridnumber);

                const itemName = (item.callinglineid || "").trim();
                const persons = peopleByNumber.get(workOrMobile) || [];

                if (canUseCallerId && itemName) {
                    const bestMatch = persons.find(p => checkNameMatch(itemName, p));
                    matchedName = bestMatch ? `${bestMatch.firstName} ${bestMatch.lastName}` : itemName;
                } else {
                    matchedName = persons.length > 0 ? `${persons[0].firstName} ${persons[0].lastName}` : "";
                }

                // 🔥 FILTRE STRICT : On rejette si l'appel ne correspond pas à la personne 🔥
                if (personNames && personNames.length > 0) {
                    const nameToVerify = itemName ? itemName.toLowerCase() : matchedName.toLowerCase();
                    const isRequestedPerson = personNames.some(filterName =>
                        nameToVerify.includes(filterName) || filterName.includes(nameToVerify)
                    );
                    if (!isRequestedPerson) {
                        return null; // Détruit l'appel !
                    }
                }
            }
        } else if (isIncoming) {
            const calledNum = item.callednumber;
            if (knownNumbers.has(calledNum)) {
                matchedPost = calledNum;
                const persons = peopleByNumber.get(calledNum) || [];

                const itemName = (item.calledlineid || "").trim();
                if (canUseCallerId && itemName) {
                    const bestMatch = persons.find(p => checkNameMatch(itemName, p));
                    matchedName = bestMatch ? `${bestMatch.firstName} ${bestMatch.lastName}` : itemName;
                } else {
                    matchedName = persons.length > 0 ? `${persons[0].firstName} ${persons[0].lastName}` : "";
                }

                // 🔥 FILTRE STRICT AUSSI SUR LES ENTRANTS 🔥
                if (personNames && personNames.length > 0) {
                    const nameToVerify = itemName ? itemName.toLowerCase() : matchedName.toLowerCase();
                    const isRequestedPerson = personNames.some(filterName =>
                        nameToVerify.includes(filterName) || filterName.includes(nameToVerify)
                    );
                    if (!isRequestedPerson) {
                        return null; // Détruit l'appel entrant qui ne lui est pas destiné
                    }
                }
            }
        }

        if (matchedPost) {
            return { post: matchedPost, name: matchedName, isCallerId: isCallerIdMatch, isOutgoing, isIncoming };
        }
        return null;
    };

    const handleGraphTypeTrends = (data) => {
        const canUseCallerId = user && user.holdingId === 33;
        const personNames = normalizedFilteredPosts.personNames;

        const peopleFlat = people.flat();
        const peopleByNumber = getPeopleByNumberMap(peopleFlat);
        const knownNumbers = new Set(peopleByNumber.keys());

        const postCountsByDate = {};
        const uniqueDates = new Set();

        data.forEach(item => {
            const callDate = new Date(item.starttime).toLocaleDateString();

            // 🔀 BIFURCATION : 33 vs AUTRES
            if (canUseCallerId) {
                const entity = extractCallEntity(item, knownNumbers, peopleByNumber, canUseCallerId, personNames);
                if (entity) {
                    if (!postCountsByDate[callDate]) postCountsByDate[callDate] = new Set();
                    postCountsByDate[callDate].add(entity.post);
                    uniqueDates.add(callDate);
                }
            } else {
                // LOGIQUE CLASSIQUE STRICTE DE TON ANCIEN CODE
                let matchedPost = null;
                if (knownNumbers.has(item.callingnumber)) {
                    matchedPost = item.callingnumber;
                }
                if (matchedPost) {
                    if (!postCountsByDate[callDate]) postCountsByDate[callDate] = new Set();
                    postCountsByDate[callDate].add(matchedPost);
                    uniqueDates.add(callDate);
                }
            }
        });

        const maxPostsDate = Object.keys(postCountsByDate).reduce((maxDate, currentDate) => {
            return postCountsByDate[currentDate].size > postCountsByDate[maxDate].size ? currentDate : maxDate;
        }, Object.keys(postCountsByDate)[0] || "");

        const maxCallsData = data.filter(item => new Date(item.starttime).toLocaleDateString() === maxPostsDate);

        const aggregatedResults = Array.from({ length: 24 }, (_, hour) => ({
            key: `${hour}:00`, date: "",
            Outgoing: { total: 0, durationTotal: 0, averageduration: 0, response: 0, averageresponse: 0, noresponse: 0 },
            Incoming: { total: 0, durationTotal: 0, averageduration: 0, response: 0, averageresponse: 0, noresponse: 0 },
            Total: { durationTotal: 0, responseTotal: 0 }
        }));

        maxCallsData.forEach(item => {
            const date = new Date(item.starttime);
            const hour = date.getHours();
            const dateString = date.toLocaleDateString();

            if (!aggregatedResults[hour].date) {
                aggregatedResults[hour].date = dateString;
            }

            // 🔀 BIFURCATION : 33 vs AUTRES
            if (canUseCallerId) {
                const entity = extractCallEntity(item, knownNumbers, peopleByNumber, canUseCallerId, personNames);
                if (entity) {
                    const direction = entity.isOutgoing ? "Outgoing" : "Incoming";
                    const isResponse = item.answerindicator === "Yes";
                    const duration = Math.floor(parseInt(item.duration) || 0);
                    const ringDuration = Math.floor(parseInt(item.ringduration) || 0);

                    aggregatedResults[hour][direction].total += 1;
                    aggregatedResults[hour][direction].durationTotal += duration;
                    if (isResponse) {
                        aggregatedResults[hour][direction].response += 1;
                        aggregatedResults[hour][direction].averageresponse += ringDuration;
                    } else if (item.answerindicator === "No") {
                        aggregatedResults[hour][direction].noresponse += 1;
                    }
                }
            } else {
                // LOGIQUE CLASSIQUE STRICTE
                let matchedPost = null;
                if (knownNumbers.has(item.callingnumber)) {
                    matchedPost = item.callingnumber;
                }
                if (matchedPost) {
                    const isOutgoing = item.direction === "ORIGINATING";
                    const direction = isOutgoing ? "Outgoing" : "Incoming";
                    const isResponse = item.answerindicator === "Yes";
                    const duration = Math.floor(parseInt(item.duration) || 0);
                    const ringDuration = Math.floor(parseInt(item.ringduration) || 0);

                    aggregatedResults[hour][direction].total += 1;
                    aggregatedResults[hour][direction].durationTotal += duration;
                    if (isResponse) {
                        aggregatedResults[hour][direction].response += 1;
                        aggregatedResults[hour][direction].averageresponse += ringDuration;
                    } else if (item.answerindicator === "No") {
                        aggregatedResults[hour][direction].noresponse += 1;
                    }
                }
            }
        });

        aggregatedResults.forEach(hourData => {
            ['Outgoing', 'Incoming'].forEach(direction => {
                const d = hourData[direction];
                d.averageduration = d.total > 0 ? d.durationTotal / d.total : 0;
                d.averageresponse = d.response > 0 ? d.averageresponse / d.response : 0;
            });

            const totalDuration = hourData.Outgoing.durationTotal + hourData.Incoming.durationTotal;

            hourData.Total.durationTotal = totalDuration;
            hourData.Total.durationTotalFormatted = formatDuration(totalDuration);
            hourData.Total.responseTotal = hourData.Outgoing.response + hourData.Incoming.response + hourData.Outgoing.noresponse + hourData.Incoming.noresponse;

            hourData.Outgoing.durationTotal = formatDuration(hourData.Outgoing.durationTotal);
            hourData.Outgoing.averageduration = formatDuration(hourData.Outgoing.averageduration);
            hourData.Incoming.durationTotal = formatDuration(hourData.Incoming.durationTotal);
            hourData.Incoming.averageduration = formatDuration(hourData.Incoming.averageduration);
            hourData.Outgoing.averageresponse = formatDuration(hourData.Outgoing.averageresponse);
            hourData.Incoming.averageresponse = formatDuration(hourData.Incoming.averageresponse);
        });

        setTypeTrends({ maxCallsDate: maxPostsDate, maxCallsData, aggregatedResults });
    };

    const handleGraphDataTypeTrends = (data) => {
        const canUseCallerId = user && user.holdingId === 33;
        const personNames = normalizedFilteredPosts.personNames;

        const peopleFlat = people.flat();
        const peopleByNumber = getPeopleByNumberMap(peopleFlat);
        const knownNumbers = new Set(peopleByNumber.keys());

        let postData;

        // 🔀 BIFURCATION : HOLDING 33
        if (canUseCallerId) {
            const activeEntitiesMap = new Map();

            data.forEach(item => {
                const entity = extractCallEntity(item, knownNumbers, peopleByNumber, canUseCallerId, personNames);

                if (entity) {
                    const entityKey = entity.post;

                    if (!activeEntitiesMap.has(entityKey)) {
                        activeEntitiesMap.set(entityKey, {
                            post: entity.post,
                            callinglineid: entity.name,
                            isCallerId: entity.isCallerId,
                            Outgoing: { total: 0, durationTotal: 0, averageduration: 0, response: 0, averageresponse: 0, noresponse: 0 },
                            Incoming: { total: 0, durationTotal: 0, averageduration: 0, response: 0, averageresponse: 0, noresponse: 0 },
                            Total: { durationTotal: 0, responseTotal: 0 },
                            data: []
                        });
                    }

                    const postDataRef = activeEntitiesMap.get(entityKey);
                    postDataRef.data.push(item);

                    const direction = entity.isOutgoing ? "Outgoing" : "Incoming";
                    const duration = Math.floor(parseInt(item.duration) || 0);
                    const ringDuration = Math.floor(parseInt(item.ringduration) || 0);

                    postDataRef[direction].total += 1;
                    postDataRef[direction].durationTotal += duration;

                    if (item.answerindicator === "Yes") {
                        postDataRef[direction].response += 1;
                        postDataRef[direction].averageresponse += ringDuration;
                    } else if (item.answerindicator === "No") {
                        postDataRef[direction].noresponse += 1;
                    }
                }
            });

            postData = Array.from(activeEntitiesMap.values());
        }
        // 🔀 BIFURCATION : TOUS LES AUTRES (RETOUR À L'ANCIEN CODE EXACT)
        else {
            const uniquePosts = Array.from(knownNumbers);
            postData = uniquePosts.map(post => ({
                post,
                callinglineid: "",
                isCallerId: false,
                Outgoing: { total: 0, durationTotal: 0, averageduration: 0, response: 0, averageresponse: 0, noresponse: 0 },
                Incoming: { total: 0, durationTotal: 0, averageduration: 0, response: 0, averageresponse: 0, noresponse: 0 },
                Total: { durationTotal: 0, responseTotal: 0 },
                data: []
            }));

            data.forEach(item => {
                let matchedPost = null;

                if (uniquePosts.includes(item.callingnumber)) {
                    matchedPost = item.callingnumber;
                }

                const postCalled = uniquePosts.find(p => p === item.callednumber);

                if (matchedPost) {
                    const postIndex = postData.findIndex(p => p.post === matchedPost);
                    if (postIndex !== -1) {
                        postData[postIndex].data.push(item);

                        if (!postData[postIndex].callinglineid) {
                            const person = peopleFlat.find(person => person.phone.work === item.callingnumber || person.phone.mobile === item.callingnumber);
                            postData[postIndex].callinglineid = person ? `${person.firstName} ${person.lastName}` : "";
                        }

                        if (item.direction === "TERMINATING") {
                            postData[postIndex].Incoming.total += 1;
                            postData[postIndex].Incoming.durationTotal += Math.floor(parseInt(item.duration) || 0);
                            if (item.answerindicator === "Yes") {
                                postData[postIndex].Incoming.response += 1;
                                postData[postIndex].Incoming.averageresponse += Math.floor(parseInt(item.ringduration) || 0);
                            } else if (item.answerindicator === "No") {
                                postData[postIndex].Incoming.noresponse += 1;
                            }
                        } else if (item.direction === "ORIGINATING") {
                            postData[postIndex].Outgoing.total += 1;
                            postData[postIndex].Outgoing.durationTotal += Math.floor(parseInt(item.duration) || 0);
                            if (item.answerindicator === "Yes") {
                                postData[postIndex].Outgoing.response += 1;
                                postData[postIndex].Outgoing.averageresponse += Math.floor(parseInt(item.ringduration) || 0);
                            } else if (item.answerindicator === "No") {
                                postData[postIndex].Outgoing.noresponse += 1;
                            }
                        }
                    }
                }

                if (postCalled && !matchedPost) {
                    const postIndex = postData.findIndex(p => p.post === postCalled);
                    if (postIndex !== -1) {
                        postData[postIndex].data.push(item);
                        postData[postIndex].Incoming.total += 1;
                        postData[postIndex].Incoming.durationTotal += Math.floor(parseInt(item.duration) || 0);
                        if (item.answerindicator === "Yes") {
                            postData[postIndex].Incoming.response += 1;
                            postData[postIndex].Incoming.averageresponse += Math.floor(parseInt(item.ringduration) || 0);
                        } else if (item.answerindicator === "No") {
                            postData[postIndex].Incoming.noresponse += 1;
                        }
                    }
                }
            });

            postData = postData.filter(post => post.data.length > 0);
        }

        // Formater les données (Commun à tout le monde)
        postData.forEach(post => {
            let Outgoing = post.Outgoing;
            let Incoming = post.Incoming;
            let Total = post.Total;

            Outgoing.averageduration = Outgoing.total > 0 ? Outgoing.durationTotal / Outgoing.total : 0;
            Outgoing.averageresponse = Outgoing.response > 0 ? Outgoing.averageresponse / Outgoing.response : 0;

            Incoming.averageduration = Incoming.total > 0 ? Incoming.durationTotal / Incoming.total : 0;
            Incoming.averageresponse = Incoming.response > 0 ? Incoming.averageresponse / Incoming.response : 0;

            Total.responseTotal = Outgoing.response + Incoming.response + Outgoing.noresponse + Incoming.noresponse;
            Total.durationTotal = Outgoing.durationTotal + Incoming.durationTotal;

            Outgoing.durationTotal = formatDuration(Outgoing.durationTotal);
            Outgoing.averageduration = formatDuration(Outgoing.averageduration);
            Outgoing.averageresponse = formatDuration(Outgoing.averageresponse);

            Incoming.durationTotal = formatDuration(Incoming.durationTotal);
            Incoming.averageduration = formatDuration(Incoming.averageduration);
            Incoming.averageresponse = formatDuration(Incoming.averageresponse);

            Total.durationTotalFormatted = formatDuration(Total.durationTotal);
        });

        setDataTypeTrends(postData);
        const formattedData = formatCombinedDataForExcel(typeTrends.aggregatedResults, postData, currentLang);
        setDataGraph(formattedData);
    };

    const formatCombinedDataForExcel = (aggregatedResults, postData, lang) => {
        const headers = ["", "Sortant", "Entrant", "Total", "", "", "", "", "", "", ""];
        const canUseCallerId = user && user.holdingId === 33;

        const formattedData = [headers];

        const keysMapping = [
            { key: "response", label: "Nombre d'appels répondu" },
            { key: "averageresponse", label: "Durée moyenne pour répondre" },
            { key: "noresponse", label: "Nombre d'appels non répondu" },
            { key: "durationTotal", label: "Durée totale d'appels" },
            { key: "averageduration", label: "Durée moyenne d'appels" },
        ];
        const keysMappingTotal = [
            { key: "durationTotal", label: "Nombre total des appels répondu" },
            { key: "responseTotal", label: "Durée totale des appels" },
        ];

        aggregatedResults.forEach(item => {
            if (item.date) {
                formattedData.push([`Date: ${item.key} ${item.date}`, "", "", ""]);

                keysMapping.forEach(({ key, label }) => {
                    const row = [
                        label,
                        item.Outgoing ? item.Outgoing[key] : null,
                        item.Incoming ? item.Incoming[key] : null,
                        null
                    ];
                    formattedData.push(row);
                });

                keysMappingTotal.forEach(({ key, label }) => {
                    const row = [
                        label,
                        null,
                        null,
                        key === "durationTotal" && item.Total ? formatDuration(item.Total[key]) : item.Total ? item.Total[key] : null
                    ];
                    formattedData.push(row);
                });
            };
        });

        postData.forEach(post => {
            const displayName = (post.isCallerId ? "⛔ " : "") + (post.callinglineid || post.post);
            formattedData.push([displayName, "", "", "", "", ""]);

            formattedData.push([
                "Nombre d'appels répondu",
                post.Outgoing.response || 0,
                post.Incoming.response || 0,
                ''
            ]);

            formattedData.push([
                "Durée moyenne pour répondre",
                post.Outgoing.averageresponse || "00:00:00",
                post.Incoming.averageresponse || "00:00:00",
                ""
            ]);

            formattedData.push([
                "Nombre d'appels non répondu",
                post.Outgoing.noresponse || 0,
                post.Incoming.noresponse || 0,
                ""
            ]);

            formattedData.push([
                "Durée totale d'appels",
                post.Outgoing.durationTotal || "00:00:00",
                post.Incoming.durationTotal || "00:00:00",
                ""
            ]);

            formattedData.push([
                "Durée moyenne d'appels",
                post.Outgoing.averageduration || "00:00:00",
                post.Incoming.averageduration || "00:00:00",
                ""
            ]);

            formattedData.push([
                "Durée totale des appels",
                "",
                "",
                post.Total.durationTotalFormatted || "00:00:00",
            ]);

            formattedData.push([
                "Nombre totale des appels répondu",
                "",
                "",
                post.Total.responseTotal || "00:00:00",
            ]);

            const detailedHeaders = [
                "Date", "Durée de communication", "Poste", "Nom poste", "Numéro",
                ...(canUseCallerId ? ["Numéro affiché"] : []),
                "Nom", "Origine", "Résultat", "Durée d'attente", "Type"
            ];
            formattedData.push(detailedHeaders);

            post.data.forEach(call => {
                formattedData.push([
                    convertDateWithDayName(call.starttime, lang) || "",
                    call.duration || "0",
                    call.callingnumber || "",
                    post.callinglineid || "",
                    call.callednumber || "",
                    ...(canUseCallerId ? [call.calleridnumber || ""] : []),
                    call.calledlineid || "",
                    getCallType(call.calltype),
                    call.answerindicator || "No",
                    call.ringduration || "0",
                    getCallDirection(call.direction) || ""
                ]);
            });

            formattedData.push([]);
        });
        setFormattedHeader(true);
        return formattedData;
    };

    const getCallDirection = (direction) => {
        switch (direction) {
            case 'TERMINATING': return t('Entrant');
            case 'ORIGINATING': return t('Sortant');
            default: return direction;
        }
    }

    const getCallType = (callType) => {
        switch (callType) {
            case 'SIP_ENTERPRISE': return 'Interne';
            case 'SIP_NATIONAL': return 'National';
            case 'SIP_MOBILE': return 'Mobile';
            case 'SIP_INTERNATIONAL': return 'International';
            case 'SIP_PREMIUM': return 'Premium';
            case 'SIP_INBOUND': return 'Entrant';
            case 'SIP_OPERATOR': return 'Opérateur';
            case 'SIP_TOLLFREE': return 'Sans frais';
            case 'SIP_MEETING': return 'Réunion';
            case 'UNKNOWN': return 'Inconnu';
            default: return callType;
        }
    }

    const formatDuration = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    let indexOfLastItem, indexOfFirstItem;
    if (currentPage === 1) {
        indexOfFirstItem = 0;
        indexOfLastItem = 0;
    } else {
        indexOfLastItem = (currentPage - 2) * postItemsPerPage + postItemsPerPage;
        indexOfFirstItem = (currentPage - 2) * postItemsPerPage;
    }

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const paginatePost = (postIndex, pageNumber) => {
        setCurrentPostPage({ ...currentPostPage, [postIndex]: pageNumber });
    };

    const handleTypeChartChange = (newTypeChart) => {
        setSelectedTypeChart(newTypeChart);
    };

    const changePostItemPerPage = (change) => {
        setPostItemPerPage(prevValue => {
            const newValue = prevValue + change;
            if (newValue < 1) return 1;
            if (newValue > 15) return 15;
            return newValue;
        });
    };

    if (!typeTrends) {
        return (
            <div>{t("Aucune donnée disponible")}</div>
        )
    }

    const canUseCallerId = user && user.holdingId === 33;

    return (
        <div className="typeTrends">
            {typeTrends && dataTypeTrends ? (
                <section className="typeTrends-element">
                    <div className="pagination">
                        <button
                            onClick={() => paginate(currentPage > 1 ? currentPage - 1 : currentPage)}
                            disabled={currentPage === 1}
                        >
                            {t("Précédent")}
                        </button>
                        <button
                            onClick={() =>
                                paginate(
                                    currentPage < Math.ceil(dataTypeTrends.length / postItemsPerPage) + 1
                                        ? currentPage + 1
                                        : currentPage
                                )
                            }
                            disabled={currentPage === Math.ceil(dataTypeTrends.length / postItemsPerPage) + 1}
                        >
                            {t("Suivant")}
                        </button>
                    </div>
                    {currentPage === 1 ? (
                        <div className="typeTrends-element-charts">
                            <div className="page-chart-webex-total-content-buttons">
                                <div className="page-chart-webex-total-buttons">
                                    <button className={`${selectedTypeChart === 'trends-bar' ? 'button-target-data' : ''}`} onClick={() => handleTypeChartChange('trends-bar')}><StackedBarChart /></button>
                                    <button className={`${selectedTypeChart === 'trends-line' ? 'button-target-data' : ''}`} onClick={() => handleTypeChartChange('trends-line')}><StackedLineChart /></button>
                                </div>
                            </div>
                            {Object.values(
                                typeTrends.aggregatedResults.reduce((acc, item) => {
                                    acc[item.key] = item.Outgoing.response;
                                    return acc;
                                }, {})
                            ).some(value => value !== 0) && (
                                    <div>
                                        <ChartsWebex
                                            key={theme}
                                            dataForChart={typeTrends.aggregatedResults.reduce((acc, item) => {
                                                acc[item.key] = item.Outgoing.response;
                                                return acc;
                                            }, {})}
                                            allLabels={Object.values(typeTrends.aggregatedResults).map(item => item.key)}
                                            allDates={Object.values(typeTrends.aggregatedResults).map(item => item.date)}
                                            typeOfChart={selectedTypeChart}
                                            titleChart={t("Sortant - Nombre d'appels répondus")}
                                            theme={theme}
                                        />
                                    </div>
                                )}
                            {Object.values(
                                typeTrends.aggregatedResults.reduce((acc, item) => {
                                    acc[item.key] = item.Outgoing.noresponse;
                                    return acc;
                                }, {})
                            ).some(value => value !== 0) && (
                                    <div>
                                        <ChartsWebex
                                            key={theme}
                                            dataForChart={typeTrends.aggregatedResults.reduce((acc, item) => {
                                                acc[item.key] = item.Outgoing.noresponse;
                                                return acc;
                                            }, {})}
                                            allLabels={Object.values(typeTrends.aggregatedResults).map(item => item.key)}
                                            allDates={Object.values(typeTrends.aggregatedResults).map(item => item.date)}
                                            typeOfChart={selectedTypeChart}
                                            titleChart={t("Sortant - Nombre d'appels non répondus")}
                                            theme={theme}
                                        />
                                    </div>
                                )}
                            {Object.values(
                                typeTrends.aggregatedResults.reduce((acc, item) => {
                                    acc[item.key] = item.Incoming.response;
                                    return acc;
                                }, {})
                            ).some(value => value !== 0) && (
                                    <div>
                                        <ChartsWebex
                                            key={theme}
                                            dataForChart={typeTrends.aggregatedResults.reduce((acc, item) => {
                                                acc[item.key] = item.Incoming.response;
                                                return acc;
                                            }, {})}
                                            allLabels={Object.values(typeTrends.aggregatedResults).map(item => item.key)}
                                            allDates={Object.values(typeTrends.aggregatedResults).map(item => item.date)}
                                            typeOfChart={selectedTypeChart}
                                            titleChart={t("Entrant - Nombre d'appels répondus")}
                                            theme={theme}
                                        />
                                    </div>
                                )}
                            {Object.values(
                                typeTrends.aggregatedResults.reduce((acc, item) => {
                                    acc[item.key] = item.Incoming.noresponse;
                                    return acc;
                                }, {})
                            ).some(value => value !== 0) && (
                                    <div>
                                        <ChartsWebex
                                            key={theme}
                                            dataForChart={typeTrends.aggregatedResults.reduce((acc, item) => {
                                                acc[item.key] = item.Incoming.noresponse;
                                                return acc;
                                            }, {})}
                                            allLabels={Object.values(typeTrends.aggregatedResults).map(item => item.key)}
                                            allDates={Object.values(typeTrends.aggregatedResults).map(item => item.date)}
                                            typeOfChart={selectedTypeChart}
                                            titleChart={t("Entrant - Nombre d'appels non répondus")}
                                            theme={theme}
                                        />
                                    </div>
                                )}
                            {Object.values(
                                typeTrends.aggregatedResults.reduce((acc, item) => {
                                    acc[item.key] = item.Total.durationTotal;
                                    return acc;
                                }, {})
                            ).some(value => value !== 0) && (
                                    <div>
                                        <ChartsWebex
                                            key={theme}
                                            dataForChart={typeTrends.aggregatedResults.reduce((acc, item) => {
                                                acc[item.key] = item.Total.durationTotal;
                                                return acc;
                                            }, {})}
                                            allLabels={Object.values(typeTrends.aggregatedResults).map(item => item.key)}
                                            allDates={Object.values(typeTrends.aggregatedResults).map(item => item.date)}
                                            typeOfChart={selectedTypeChart}
                                            titleChart={t("Total - Durée totale des appels")}
                                            mesurementUnit={'formatDuration'}
                                            theme={theme}
                                        />
                                    </div>
                                )}
                            {Object.values(
                                typeTrends.aggregatedResults.reduce((acc, item) => {
                                    acc[item.key] = item.Total.responseTotal;
                                    return acc;
                                }, {})
                            ).some(value => value !== 0) && (
                                    <div>
                                        <ChartsWebex
                                            key={theme}
                                            dataForChart={typeTrends.aggregatedResults.reduce((acc, item) => {
                                                acc[item.key] = item.Total.responseTotal;
                                                return acc;
                                            }, {})}
                                            allLabels={Object.values(typeTrends.aggregatedResults).map(item => item.key)}
                                            allDates={Object.values(typeTrends.aggregatedResults).map(item => item.date)}
                                            typeOfChart={selectedTypeChart}
                                            titleChart={t('Total - Réponse totale des appels')}
                                            theme={theme}
                                        />
                                    </div>
                                )}
                        </div>
                    ) : (
                        <>
                            <div className="typeTrends-element-firstData">
                                <table className="typeTrends-sortants">
                                    <thead>
                                        <tr className="sortant"><th colSpan={6}>{t("Sortants")}</th></tr>
                                        <tr className="sortant-head">
                                            <th>{t("Date")}</th>
                                            <th title={t("Nombre d'appels répondu")}>{t("Nombre d'appels répondu")}</th>
                                            <th title={t("Durée moyenne pour répondre")}>{t("Durée moyenne pour répondre")}</th>
                                            <th title={t("Nombre d'appels non répondu")}>{t("Nombre d'appels non répondu")}</th>
                                            <th title={t("Durée totale d'appels")}>{t("Durée totale d'appels")}</th>
                                            <th title={t("Durée moyenne d'appels")}>{t("Durée moyenne d'appels")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {typeTrends.aggregatedResults.map((hourData, index) => (
                                            (hourData.date && (
                                                <tr className="sortant-body" key={index}>
                                                    <td>{hourData.key} {hourData.date}</td>
                                                    <td>{hourData.Outgoing.response}</td>
                                                    <td>{hourData.Outgoing.averageresponse}</td>
                                                    <td>{hourData.Outgoing.noresponse}</td>
                                                    <td>{hourData.Outgoing.durationTotal}</td>
                                                    <td>{hourData.Outgoing.averageduration}</td>
                                                </tr>
                                            ))
                                        ))}
                                    </tbody>
                                </table>
                                <table className="typeTrends-entrants">
                                    <thead>
                                        <tr className="entrant"><th colSpan={6}>{t("Entrants")}</th></tr>
                                        <tr>
                                            <th title={t("Nombre d'appels répondu")}>{t("Nombre d'appels répondu")}</th>
                                            <th title={t("Durée moyenne pour répondre")}>{t("Durée moyenne pour répondre")}</th>
                                            <th title={t("Nombre d'appels non répondu")}>{t("Nombre d'appels non répondu")}</th>
                                            <th title={t("Durée totale d'appels")}>{t("Durée totale d'appels")}</th>
                                            <th title={t("Durée moyenne d'appels")}>{t("Durée moyenne d'appels")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {typeTrends.aggregatedResults.map((hourData, index) => (
                                            (hourData.date && (
                                                <tr key={index}>
                                                    <td>{hourData.Incoming.response}</td>
                                                    <td>{hourData.Incoming.averageresponse}</td>
                                                    <td>{hourData.Incoming.noresponse}</td>
                                                    <td>{hourData.Incoming.durationTotal}</td>
                                                    <td>{hourData.Incoming.averageduration}</td>
                                                </tr>
                                            ))
                                        ))}
                                    </tbody>
                                </table>
                                <table className="typeTrends-total">
                                    <thead>
                                        <tr className="total"><th colSpan={5}>{t("Total")}</th></tr>
                                        <tr className="typeTrends-total-head">
                                            <th title={t("Durée totale des appels")}>{t("Durée totale des appels")}</th>
                                            <th title={t("Nombre totale d'appels")}>{t("Nombre totale d'appels")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {typeTrends.aggregatedResults.map((hourData, index) => (
                                            (hourData.date && (
                                                <tr className="typeTrends-total-body" key={index}>
                                                    <td>{hourData.Total.durationTotalFormatted}</td>
                                                    <td>{hourData.Total.responseTotal}</td>
                                                </tr>
                                            ))
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {dataTypeTrends.slice(indexOfFirstItem, indexOfLastItem).map((post, index) => (
                                <section className="typeTrends-element" key={`${post.post}_${post.callinglineid}_${index}`}>
                                    <div>
                                        <table className="typeTrends-sortants">
                                            <thead>
                                                <tr className="sortant"><th colSpan={6}>{t("Sortants")}</th></tr>
                                                <tr className="sortant-head">
                                                    <th>{t("Poste")}</th>
                                                    <th title={t("Nombre d'appels répondu")}>{t("Appels rép...")}</th>
                                                    <th title={t("Durée moyenne pour répondre")}>{t("Durée moy pour rép...")}</th>
                                                    <th title={t("Nombre d'appels non répondu")}>{t("Appels non rép...")}</th>
                                                    <th title={t("Durée totale d'appels")}>{t("Durée totale")}</th>
                                                    <th title={t("Durée moyenne d'appels")}>{t("Durée moy.")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr className="sortant-body">
                                                    <td>
                                                        {post.isCallerId ? "⛔ " : ""}
                                                        {post.callinglineid || post.post}
                                                    </td>
                                                    <td>{post.Outgoing.response}</td>
                                                    <td>{post.Outgoing.averageresponse}</td>
                                                    <td>{post.Outgoing.noresponse}</td>
                                                    <td>{post.Outgoing.durationTotal}</td>
                                                    <td>{post.Outgoing.averageduration}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <table className="typeTrends-entrants">
                                            <thead>
                                                <tr className="entrant"><th colSpan={6}>{t("Entrants")}</th></tr>
                                                <tr>
                                                    <th title={t("Nombre d'appels répondu")}>{t("Nombre d'appels répondu")}</th>
                                                    <th title={t("Durée moyenne pour répondre")}>{t("Durée moyenne pour répondre")}</th>
                                                    <th title={t("Nombre d'appels non répondu")}>{t("Nombre d'appels non répondu")}</th>
                                                    <th title={t("Durée totale d'appels")}>{t("Durée totale d'appels")}</th>
                                                    <th title={t("Durée moyenne d'appels")}>{t("Durée moyenne d'appels")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>{post.Incoming.response}</td>
                                                    <td>{post.Incoming.averageresponse}</td>
                                                    <td>{post.Incoming.noresponse}</td>
                                                    <td>{post.Incoming.durationTotal}</td>
                                                    <td>{post.Incoming.averageduration}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <table className="typeTrends-total">
                                            <thead>
                                                <tr className="total"><th colSpan={6}>{t("Total")}</th></tr>
                                                <tr>
                                                    <th title={t("Durée totale des appels")}>{t("Durée totale des appels")}</th>
                                                    <th title={t("Nombre totale d'appels")}>{t("Nombre totale d'appels")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>{post.Total.durationTotalFormatted}</td>
                                                    <td>{post.Total.responseTotal}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <p className="postSurvey-post-title">
                                        {t("Détails des appels du numéro ")}
                                        {post.post} :
                                    </p>
                                    <div>
                                        <table className="typeTrends-post">
                                            <thead>
                                                <tr className="typeTrends-post-head">
                                                    <th>{t("Date")}</th>
                                                    <th>{t("Durée de communication")}</th>
                                                    <th>{t("Poste")}</th>
                                                    <th>{t("Nom poste")}</th>
                                                    <th>{t("Numéro")}</th>

                                                    {canUseCallerId && <th>{t("Numéro affiché")}</th>}

                                                    <th>{t("Nom")}</th>
                                                    <th>{t("Origine")}</th>
                                                    <th>{t("Résultat")}</th>
                                                    <th title={t("Durée d'attente")}>{t("Durée d'attente")}</th>
                                                    <th>{t("Type")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {post.data.slice((currentPostPage[post.post] || 1) * postItemsPerPage - postItemsPerPage,
                                                    (currentPostPage[post.post] || 1) * postItemsPerPage).map((item, idx) => (
                                                        <tr className="typeTrends-post-body" key={idx}>
                                                            <td>{item.starttime}</td>
                                                            <td>{item.duration}</td>
                                                            <td>{item.callingnumber}</td>
                                                            <td>{item.callinglineid}</td>
                                                            <td>{item.callednumber}</td>

                                                            {canUseCallerId && <td>{item.calleridnumber}</td>}

                                                            <td>{item.calledlineid}</td>
                                                            <td>
                                                                {
                                                                    item.calltype === 'SIP_ENTERPRISE' ? t('Interne') :
                                                                        item.calltype === 'SIP_NATIONAL' ? 'National' :
                                                                            item.calltype === 'SIP_MOBILE' ? 'Mobile' :
                                                                                item.calltype === 'UNKNOWN' ? t('Inconnu') :
                                                                                    item.calltype === 'SIP_PREMIUM' ? 'Prenium' :
                                                                                        item.calltype === 'SIP_INTERNATIONAL' ? t('International') :
                                                                                            item.calltype === 'SIP_INBOUND' ? t('Entrant') :
                                                                                                item.calltype === 'SIP_OPERATOR' ? t('Opérateur') :
                                                                                                    item.calltype === 'SIP_TOLLFREE' ? t('Sans frais') :
                                                                                                        item.calltype === 'SIP_MEETING' ? t('Réunion') :
                                                                                                            item.calltype
                                                                }
                                                            </td>
                                                            <td>{item.answerindicator}</td>
                                                            <td>{item.ringduration}</td>
                                                            <td>{item.direction}</td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="pagination">
                                        <button
                                            onClick={() =>
                                                paginatePost(post.post, (currentPostPage[post.post] || 1) > 1 ? (currentPostPage[post.post] || 1) - 1 : (currentPostPage[post.post] || 1))
                                            }
                                            disabled={(currentPostPage[post.post] || 1) === 1}
                                        >
                                            {t("Précédent")}
                                        </button>
                                        <button
                                            onClick={() =>
                                                paginatePost(post.post,
                                                    (currentPostPage[post.post] || 1) <
                                                        Math.ceil(post.data.length / postItemsPerPage)
                                                        ? (currentPostPage[post.post] || 1) + 1
                                                        : (currentPostPage[post.post] || 1))
                                            }
                                            disabled={
                                                (currentPostPage[post.post] || 1) ===
                                                Math.ceil(post.data.length / postItemsPerPage)
                                            }
                                        >
                                            {t("Suivant")}
                                        </button>
                                        <div className="surveyPost-post-numbers">
                                            <button onClick={() => changePostItemPerPage(-1)} disabled={postItemsPerPage === 1}>
                                                -
                                            </button>
                                            <span>{postItemsPerPage}</span>
                                            <button onClick={() => changePostItemPerPage(+1)} disabled={postItemsPerPage === 15}>
                                                +
                                            </button>
                                        </div>
                                    </div>
                                </section>
                            ))}
                        </>
                    )}
                </section>
            ) : (
                <Loader />
            )}
        </div>
    )
}